// @flow
import * as React from 'react';
import Popover, { type Props as OuterPopoverProps } from '@ta/overlays.popover';
import Button from '@ta/styleguide.button';
import styles from './ConfirmationPopover.less';

type UsedProps = {|
  arrowType: any,
  local: any,
  contents: any,
  closeButton: any,
|};
/* A component that renders a floating popover prompting users to confirm an action */
type PopoverProps = $Diff<OuterPopoverProps, UsedProps>;

/** Props for the clients */
export type Props = {|
  /** The text to show at the top of the popover */
  header: string | (() => string),
  /** The text to show in the main body of the popover */
  description: string | (() => string),
  /** The text to show on the cancel button - the one on the left */
  cancel: string | (() => string),
  /** The text to show on the confirm button - the one on the right */
  confirm: string | (() => string),
  /** optional callback to execute on confirm press */
  onConfirm: ?(event: SyntheticEvent<*>) => void,
  /** optional callback to execute on cancel press */
  onCancel: ?(event: SyntheticEvent<*>) => void,
  /** other popover props, minus arrowType, local, contents, closeButton */
  ...PopoverProps,
|};

const functionWrapper = (close, fn) => (event: SyntheticEvent<*>) => {
  if (fn) {
    fn(event);
  }
  close();
};

const textify = val => {
  if (typeof val === 'function') {
    return val();
  }
  return val;
};

const ConfirmationPopover = ({
  header,
  description,
  cancel,
  confirm,
  onConfirm,
  onCancel,
  ...popoverProps
}: Props) => {
  const contents = close => (
    <div className={styles.container}>
      <div className={styles.header}>{textify(header)}</div>
      <div className={styles.text}>{textify(description)}</div>
      <div className={styles.social_button_wrapper}>
        <span className={styles.secondary_wrap}>
          <Button type="secondary" onClick={functionWrapper(close, onCancel)} size="fullwidth">
            {textify(cancel)}
          </Button>
        </span>
        <span className={styles.primary_wrap} data-test-target={`confirm-${textify(confirm)}`}>
          <Button type="primary" onClick={functionWrapper(close, onConfirm)} size="fullwidth">
            {textify(confirm)}
          </Button>
        </span>
      </div>
    </div>
  );
  return (
    <Popover {...popoverProps} contents={contents} arrowType="none" local closeButton={false} />
  );
};

ConfirmationPopover.defaultProps = {
  above: undefined,
  below: undefined,
  rightOf: undefined,
  leftOf: undefined,
  alignEdge: undefined,
  shiftX: 0,
  shiftY: 0,
  shiftArrow: 0,
  fixed: false,
  onCancel: null,
  onConfirm: null,
};

export default ConfirmationPopover;
